/**
 * Brand Fidelity Scoring — grade any agentic output against the Brand Brain
 * and return a deterministic 0..1 score with CONCRETE violations.
 *
 * This is "brand fidelity as a metric": instead of vibes ("does this sound
 * like us?"), every output gets a reproducible grade derived from the same
 * entries that composed its prompt (see ./compose.ts). The score feeds the
 * earned-autonomy ladder (agent/autonomyEarnUp spirit: consistently
 * high-fidelity outputs are the evidence that earns an agent more rope) and
 * the violations list renders as concrete receipts, e.g.
 * `used banned phrase: “synergy”`.
 *
 * Enforceable rules are derived from entry TEXT with transparent heuristics:
 *
 *   • banned lexicon — a 'lexicon' or 'directive' entry whose title carries
 *     a ban marker (banned / never say / avoid / do not use) contributes its
 *     body as a comma-separated banned-phrase list; alternatively a body of
 *     the form "Never say: synergy, leverage" contributes the phrases after
 *     the colon. Any banned phrase present in the output is a violation.
 *   • opener length — a 'directive' whose body reads like
 *     "opener … at most 12 words" (also ≤ / <= / under / no more than /
 *     max) caps the word count of the output's FIRST sentence.
 *
 * Entries with no enforceable rule are honestly skipped — they neither
 * penalize nor inflate the score. Scoring is PURE and deterministic: same
 * output + same brain + same scope → identical report. No store, no IO.
 */

import type { BrandBrain, BrandEntry, BrandEntryKind, BrandScope } from './schema';
import { getActiveByScope } from './store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** One concrete rule breach, tied to the exact entry that defines the rule. */
export interface FidelityViolation {
  entryId: string;
  kind: BrandEntryKind;
  /** Human-readable receipt, e.g. `used banned phrase: “synergy”`. */
  reason: string;
}

/** The full fidelity report for one output. */
export interface FidelityReport {
  /** 0..1 — 1 means every enforceable rule passed. Deterministic. */
  score: number;
  violations: FidelityViolation[];
  /** Ids of entries whose enforceable rules ALL passed for this output. */
  matched: string[];
}

/** Score penalty per violation, by the violated entry's kind. Exported so
 *  the grading policy is auditable, not folklore. */
export const FIDELITY_PENALTY: Readonly<Partial<Record<BrandEntryKind, number>>> = {
  lexicon: 0.3,
  directive: 0.2,
};

/** Fallback penalty for kinds without an explicit entry above. */
export const DEFAULT_FIDELITY_PENALTY = 0.2;

// ---------------------------------------------------------------------------
// Rule extraction heuristics (transparent, exported for the UI to explain)
// ---------------------------------------------------------------------------

const BAN_TITLE_MARKER = /\b(?:banned|never say|avoid|do not (?:say|use)|don'?t (?:say|use))\b/i;
const BAN_BODY_MARKER =
  /\b(?:banned(?:\s+(?:phrases|words|terms))?|never say|avoid|do not (?:say|use)|don'?t (?:say|use))\s*:\s*(.+)/i;

function splitPhrases(text: string): string[] {
  return text
    .split(/[,;\n]/)
    .map((s) => s.trim().replace(/^["'“”‘’]+|["'“”‘’.]+$/g, '').trim())
    .filter(Boolean);
}

/**
 * Banned phrases an entry contributes, or [] when it defines no ban.
 * Deterministic text heuristic — see module doc for the exact grammar.
 */
export function extractBannedPhrases(entry: BrandEntry): string[] {
  if (entry.kind !== 'lexicon' && entry.kind !== 'directive') return [];
  if (BAN_TITLE_MARKER.test(entry.title)) return splitPhrases(entry.body);
  const m = entry.body.match(BAN_BODY_MARKER);
  return m ? splitPhrases(m[1]) : [];
}

const OPENER_LIMIT_RE =
  /opener[^.\n]*?(?:≤|<=|under|at most|no more than|max(?:imum)?(?: of)?)\s*(\d+)\s*words/i;

/**
 * The opener word cap a directive imposes, or null when it imposes none.
 * Matches bodies like "Opener sentence must be at most 12 words."
 */
export function extractOpenerWordLimit(entry: BrandEntry): number | null {
  if (entry.kind !== 'directive') return null;
  const m = entry.body.match(OPENER_LIMIT_RE);
  return m ? Number.parseInt(m[1], 10) : null;
}

/** The output's first sentence (up to the first . ! ? or newline). */
export function firstSentence(output: string): string {
  const trimmed = output.trim();
  const m = trimmed.match(/^[^.!?\n]*[.!?]?/);
  return (m ? m[0] : trimmed).trim();
}

function wordCount(text: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean);
  return words.length;
}

// ---------------------------------------------------------------------------
// scoreBrandFidelity
// ---------------------------------------------------------------------------

const round4 = (x: number) => Math.round(x * 10000) / 10000;

/**
 * Grade one output against every ACTIVE brand entry routed to `scope`.
 * Returns a deterministic 0..1 score (1 = fully on-brand under every
 * enforceable rule), the concrete violations, and the ids of entries whose
 * rules all passed. Case-insensitive matching; clean output over an empty
 * brain honestly scores 1 with nothing matched (no rules ⇒ no evidence of
 * violation, never fabricated praise).
 */
export function scoreBrandFidelity(
  output: string,
  brain: BrandBrain,
  scope: BrandScope,
): FidelityReport {
  const entries = getActiveByScope(brain, scope);
  const haystack = output.toLowerCase();
  const opener = firstSentence(output);

  const violations: FidelityViolation[] = [];
  const matched: string[] = [];
  let penalty = 0;

  for (const entry of entries) {
    let hasRule = false;
    let entryViolated = false;

    const banned = extractBannedPhrases(entry);
    if (banned.length > 0) {
      hasRule = true;
      for (const phrase of banned) {
        if (phrase && haystack.includes(phrase.toLowerCase())) {
          entryViolated = true;
          violations.push({
            entryId: entry.id,
            kind: entry.kind,
            reason: `used banned phrase: “${phrase}”`,
          });
          penalty += FIDELITY_PENALTY[entry.kind] ?? DEFAULT_FIDELITY_PENALTY;
        }
      }
    }

    const openerLimit = extractOpenerWordLimit(entry);
    if (openerLimit !== null) {
      hasRule = true;
      const n = wordCount(opener);
      if (n > openerLimit) {
        entryViolated = true;
        violations.push({
          entryId: entry.id,
          kind: entry.kind,
          reason: `opener sentence is ${n} words — directive caps it at ${openerLimit}`,
        });
        penalty += FIDELITY_PENALTY[entry.kind] ?? DEFAULT_FIDELITY_PENALTY;
      }
    }

    if (hasRule && !entryViolated) matched.push(entry.id);
  }

  return {
    score: round4(Math.min(1, Math.max(0, 1 - penalty))),
    violations,
    matched,
  };
}
